import { defineDocument } from '../../schema';
export const blogFeedTheFarmComesBackForSecondsDocument = defineDocument({
    id: 'blog:feed-the-farm-comes-back-for-seconds',
    kind: 'blog',
    slug: 'feed-the-farm-comes-back-for-seconds',
    route: '/blog/feed-the-farm-comes-back-for-seconds',
    seo: {
        title: 'Feed The Farm Comes Back For Seconds',
        description: 'The barn was burned down and rebuilt as a simultaneous free-for-all with faster, better gameplay.',
        imageAsset: 'coverFeedTheFarm',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Feed The Farm Comes Back For Seconds',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'The barn was burned down and rebuilt as a simultaneous free-for-all with faster, better gameplay.',
            },
        ],
        imageAsset: 'coverFeedTheFarm',
        imageAlt: 'Feed The Farm cover art',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-07-23T00:00:00.000Z'),
        updatedAt: new Date('2026-07-23T00:00:00.000Z'),
        tags: ['games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: `Feed The Farm crapped out. After it launched in April 2026, our community found weaknesses in the gameplay, and it didn't work how we expected on stream. We tore it down to the studs and completely rebuilt the gameplay while keeping the heart of it intact.`,
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__the-trouble-with-taking-turns',
                    title: [
                        {
                            type: 'text',
                            text: 'The Trouble with Taking Turns',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'In ',
                                },
                                {
                                    type: 'link',
                                    href: '/blog/feed-the-farm-makes-a-mess-of-push-your-luck',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'the original iteration',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: `, Feed The Farm gameplay rotated turns one player at a time. The active player saw a three-card spread, picked blind, built a pot, and chose whether or not to push their luck by "feeding" on another card or "resting" and banking their points.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `Everyone else just watched. Most of a session was spent waiting for a turn to come back around. We had a sense this would be a problem, which is why we originally capped the game at eight players, but we didn't anticipate just how players would use their turns. Resting was supposed to be the sensible choice, but in practice, feeding again was chosen by players 75% of the time. Around 30% of players `,
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'never',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' chose to rest and ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'always',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' decided to feed again.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `While the odds were unlikely based on the deck composition, some players were able to win the game in a single feeding binge. It hurt the game in two ways: the waiting time stretched even longer for everyone else, and every so often the game would be over before some players even had a chance to play. The halting nature of the game was exacerbated by the number of speeches and reveal moments dedicated to player choices which made things more stop-and-go instead of more exciting and dramatic. With players always choosing to feed, it was just too much dead air on every turn.`,
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__everybody-eats-at-once',
                    title: [
                        {
                            type: 'text',
                            text: 'Everybody Eats at Once',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `The rebuild started from one rule: every player must play every turn.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `Each round deals three face-down cards. Everyone picks one of the three mystery feed cards or chooses to sit out and rest. You can switch your pick until either the timer runs out or everyone has made a choice. All three cards flip over to reveal whether the card is helpful or hurtful to the players who picked it, or causes mayhem for the group. Everyone who picked the same card gets the same result. Resting in the barn scores +1 that round, unless a Chaos card impacts them somehow. The three cards are discarded, whether or not anyone picked them, and play continues to the next round. The first player to reach 100 or more points with a unique lead wins. If multiple players are tied above 100, play continues until the tie breaks.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `Rounds are now paced on a timer. With fewer player decision points and speeches, the momentum is kept high from round to round as players race to the target score.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `The new approach required us to shuffle the deck and lose the concept of putting points at risk. Players no longer build up a pot of points to bank, but rather card points are applied directly to scores. The biggest change is that bad cards no longer wipe out a pot you build over a single turn. Instead, good and bad cards just directly add or subtract points from your total score for the game. With everyone playing at once, we also had to change the distribution of cards in the deck and points per card. Players can see the live tally of the current deck composition to help inform their risk-reward calculation for each turn. Finally, we retired Chaos cards that didn't work in the revised gameplay and introduced many new, creative ways to bring disorder to the farm.`,
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__bigger-better-faster',
                    title: [
                        {
                            type: 'text',
                            text: 'Bigger, Better, Faster',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `The player cap jumped to 16, because simultaneous rounds don't slow down as the room size grows the way turn-based ones did. Games still last 10 to 20 minutes, but rounds move faster, reveals hit harder, and nobody needs to sit on the sidelines waiting for their name to get called.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Fire up your tractor and head over to the ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Game Library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ` to play the new version of Feed The Farm. It's a real barnburner!`,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=feed-the-farm-comes-back-for-seconds.js.map